import { useQuery } from '@tanstack/react-query';
import { type JSX, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  sharePointSourceConnectionId,
  sharePointSourceCoverage,
  type SharePointSourceCoverage,
} from '@/features/workspace/knowledge/sharepoint-place-coverage.js';
import { sourceLabel } from '@/features/workspace/knowledge/knowledge-source-label.js';
import {
  setSharePointPlacePolicy,
  updateSharePointPlace,
  type SharePointPlace,
  type SharePointPlaceAutomationAccess,
  type SharePointPlaceInteractiveAccess,
} from '@/lib/api/sharepoint.js';
import {
  knowledgeSourcesQueryOptions,
  type KnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';

import type { SharePointConnectionChoice } from './connection-choice.js';

/** One place's locally-edited access levels, keyed by place id, seeded from the place on open. */
type EditedAccess = Record<
  string,
  {
    interactiveAccess: SharePointPlaceInteractiveAccess;
    automationAccess: SharePointPlaceAutomationAccess;
  }
>;

function seedEdits(places: readonly SharePointPlace[]): EditedAccess {
  const edits: EditedAccess = {};
  for (const place of places) {
    edits[place.id] = {
      interactiveAccess: place.interactiveAccess,
      automationAccess: place.automationAccess,
    };
  }
  return edits;
}

function errorText(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

/**
 * Confirms turning a connection's restriction on — spec §13. Pressing "Restrict access" is the one
 * moment this feature can break something that already works, so this dialog shows what it affects
 * before it writes `enforced: true`, in the three ways the spec requires:
 *
 * 1. Names the knowledge sources that fall outside the place set — the ones the agent will stop
 *    seeing once this connection is enforced. Computed the same way the knowledge-sources screen's own
 *    mark is: on read, from data both sides already hold, never stored.
 * 2. Warns that automations lose SharePoint, WITHOUT naming which ones — nothing in the platform
 *    records which connectors an automation uses, and whether a given one touches SharePoint at all is
 *    decided at run time by the model from its own prompt, so no stored field could answer it. A list
 *    built by guessing at prompt text would be confidently wrong, which is worse here than saying
 *    nothing: the reader is deciding whether to break their own automations.
 * 3. Lets every place's two access levels be set right here, before the button — a connection that was
 *    `read_write` becomes effectively read-only the moment enforcement turns on, since a place starts
 *    at `interactive_access: 'read'`, and that surprise belongs before the write, not after it.
 */
export function SharePointRestrictDialog({
  personalWorkspace = false,
  ...props
}: RestrictDialogProps & {
  /**
   * The owner's own personal workspace. There the lists were knowledge picks, every setting can be
   * changed later in its gear and Allow all undoes the whole step, so the confirmation is one
   * sentence rather than a review of each place.
   */
  personalWorkspace?: boolean;
}): JSX.Element {
  return personalWorkspace ? (
    <PersonalRestrictConfirm {...props} />
  ) : (
    <RestrictReview {...props} />
  );
}

interface RestrictDialogProps {
  open: boolean;
  workspaceId: string;
  connectionId: string;
  places: readonly SharePointPlace[];
  /** Every SharePoint connection this workspace reaches, for resolving a source with no `connection_id`. */
  connections: readonly SharePointConnectionChoice[];
  onOpenChange: (open: boolean) => void;
  onRestricted: () => void;
}

/** The personal-workspace confirmation: what changes, and one button that makes it. */
function PersonalRestrictConfirm({
  open,
  workspaceId,
  connectionId,
  onOpenChange,
  onRestricted,
}: RestrictDialogProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setSharePointPlacePolicy(workspaceId, connectionId, true);
      onRestricted();
      onOpenChange(false);
    } catch (reason) {
      setError(errorText(reason, 'Could not restrict this connection.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppDialog open={open} onOpenChange={onOpenChange} title="Restrict access">
      <div className="flex flex-col gap-4 pt-3">
        <p className="text-sm text-muted-foreground">
          The agent will reach only the items on these lists, in chat and in
          automations. Automations that used anything else stop working. Set
          each item’s access in its gear.
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Restricting…' : 'Restrict access'}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}

/** The full review a shared workspace gets before its reach narrows. */
function RestrictReview({
  open,
  workspaceId,
  connectionId,
  places,
  connections,
  onOpenChange,
  onRestricted,
}: RestrictDialogProps): JSX.Element {
  const [edits, setEdits] = useState<EditedAccess>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourcesQuery = useQuery({
    ...knowledgeSourcesQueryOptions(workspaceId),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setEdits(seedEdits(places));
      setError(null);
    }
  }, [open]);

  /**
   * What enforcing this connection right now, with these places, would remove — computed against a
   * policy that is ALWAYS `enforced: true` regardless of the connection's real current state, because
   * this list previews the effect of the write this dialog is about to make, not the state before it.
   */
  /**
   * Whether the knowledge list has actually arrived. An unloaded or failed list is not an empty
   * one, so until it has, the dialog claims nothing about the sources and the write stays off.
   */
  const listArrived = sourcesQuery.data !== undefined && !sourcesQuery.isError;
  const coverages = (sourcesQuery.data ?? []).map((source) => ({
    source,
    coverage: previewCoverage(source),
  }));
  const affectedSources = coverages
    .filter((entry) => entry.coverage === 'outside')
    .map((entry) => entry.source);
  /**
   * A row that may be this connection's but cannot be judged — no stored site, or a connection that
   * cannot be resolved — is as unknown as an unloaded list. Another connection's rows do not count:
   * this write does not touch them.
   */
  const sourcesKnown =
    listArrived &&
    coverages.every(
      (entry) =>
        entry.coverage !== 'unknown' ||
        isOtherConnection(
          sharePointSourceConnectionId(entry.source, connections),
        ),
    );

  function isOtherConnection(sourceConnectionId: string | null): boolean {
    return sourceConnectionId !== null && sourceConnectionId !== connectionId;
  }

  /** One source's standing against a policy that is ALWAYS enforced — the write this dialog makes. */
  function previewCoverage(source: KnowledgeSource): SharePointSourceCoverage {
    const policy = new Map([
      [
        connectionId,
        { places: [...places], enforced: true, placesLimit: 0, isOwner: true },
      ],
    ]);
    return sharePointSourceCoverage(source, connections, policy);
  }

  function accessOf(place: SharePointPlace): {
    interactiveAccess: SharePointPlaceInteractiveAccess;
    automationAccess: SharePointPlaceAutomationAccess;
  } {
    return (
      edits[place.id] ?? {
        interactiveAccess: place.interactiveAccess,
        automationAccess: place.automationAccess,
      }
    );
  }

  function setInteractiveAccess(
    placeId: string,
    value: SharePointPlaceInteractiveAccess,
  ): void {
    setEdits((current) => ({
      ...current,
      [placeId]: { ...current[placeId]!, interactiveAccess: value },
    }));
  }

  function setAutomationAccess(
    placeId: string,
    value: SharePointPlaceAutomationAccess,
  ): void {
    setEdits((current) => ({
      ...current,
      [placeId]: { ...current[placeId]!, automationAccess: value },
    }));
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await Promise.all(
        places
          .filter((place) => {
            const edited = accessOf(place);
            return (
              edited.interactiveAccess !== place.interactiveAccess ||
              edited.automationAccess !== place.automationAccess
            );
          })
          .map((place) =>
            updateSharePointPlace(place.id, {
              workspaceId,
              ...accessOf(place),
            }),
          ),
      );
      await setSharePointPlacePolicy(workspaceId, connectionId, true);
      onRestricted();
      onOpenChange(false);
    } catch (reason) {
      setError(errorText(reason, 'Could not restrict this connection.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Restrict access"
      description="Review what this affects before the agent's reach narrows to the places below."
      className="max-w-2xl"
    >
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pt-3">
        <div>
          <p className="text-sm font-medium">Knowledge sources</p>
          {!sourcesKnown ? (
            <p className="text-sm text-muted-foreground">
              {sourcesQuery.isError
                ? 'The knowledge sources could not be loaded, so what this would change is unknown. Close and try again.'
                : listArrived
                  ? 'Some knowledge sources cannot be matched to a connection yet, so what this would change is unknown.'
                  : 'Checking the knowledge sources…'}
            </p>
          ) : affectedSources.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every knowledge source registered from this connection sits inside
              the places below.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                The agent will stop seeing these knowledge sources — they stay
                indexed and searchable, but nothing keeps their access alive:
              </p>
              <ul className="mt-1 list-inside list-disc text-sm">
                {affectedSources.map((source) => (
                  <li key={source.id}>{sourceLabel(source)}</li>
                ))}
              </ul>
            </>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          Once this takes effect, automations reach only the places below whose
          automation access is on. Anything else they used through this
          connection stops working, with no error a user would connect to this
          action. Nothing on this platform records which automations use
          SharePoint, so none can be named here.
        </p>

        <div>
          <p className="text-sm font-medium">Access levels</p>
          {places.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing is on the list yet, so the agent will reach nothing in
              SharePoint until you add an item.
            </p>
          ) : (
            <div className="mt-1 flex flex-col gap-3">
              {places.map((place) => {
                const access = accessOf(place);
                return (
                  <div
                    key={place.id}
                    className="flex flex-col gap-2 rounded-lg border border-border p-3"
                  >
                    <p className="text-sm font-medium">
                      {place.alias ?? place.nameSnapshot}
                    </p>
                    <label className="flex flex-col gap-1 text-sm">
                      Interactive access
                      <select
                        className="h-10 rounded-md border border-input bg-background px-3"
                        value={access.interactiveAccess}
                        disabled={busy}
                        onChange={(event) =>
                          setInteractiveAccess(
                            place.id,
                            event.target
                              .value as SharePointPlaceInteractiveAccess,
                          )
                        }
                      >
                        <option value="read">Read</option>
                        <option value="read_write">Read and write</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      Automation access
                      <select
                        className="h-10 rounded-md border border-input bg-background px-3"
                        value={access.automationAccess}
                        disabled={busy}
                        onChange={(event) =>
                          setAutomationAccess(
                            place.id,
                            event.target
                              .value as SharePointPlaceAutomationAccess,
                          )
                        }
                      >
                        <option value="none">Off</option>
                        <option value="read">Read</option>
                        <option value="read_write">Read and write</option>
                      </select>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={busy || !sourcesKnown}
            onClick={() => void confirm()}
          >
            {busy ? 'Restricting…' : 'Restrict access'}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
